import { useId, useState } from "react";
import { candidateQualify, screenOpenResult } from "@/api/tools";
import { useRead, useWrite } from "@/api/useTool";
import { Banner, Field, Pill, ReasonDialog, Skeleton, Table } from "@/ui";

type RequirementState = "met" | "partial" | "no";
type RequirementRead = Record<string, unknown>;

/** One qualification editor for the pipeline and the application record. */
export function QualifyDialog({ rid, aid, name, onClose }: { rid: string; aid: string; name: string; onClose: () => void }) {
  const prefix = useId();
  const q = useRead(["qualification-requirements", rid, aid], () => screenOpenResult({ requisition_id: rid, application_id: aid }));
  const [reads, setReads] = useState<Record<string, { state: RequirementState | ""; evidence: string }>>({});
  const qualify = useWrite((input: Parameters<typeof candidateQualify>[0], key) => candidateQualify(input, key),
    [["application", aid], ["candidates", rid], ["requisition", rid], ["requisitions"], ["today"]]);
  const requirements = q.data?.must_haves ?? [];
  return <ReasonDialog title={`Qualify — ${name}`} label="Your read against the must-haves (one line the Hiring Manager will see)"
    confirm="Qualify" open onClose={onClose} busy={qualify.isPending} error={qualify.error}
    blockedReason={q.isLoading ? "Loading the intake requirements." : q.isError ? "The intake requirements could not be loaded. Retry before qualifying." : undefined}
    onConfirm={note => {
      if (!q.data) return;
      void qualify.mutateAsync({ requisition_id: rid, application_id: aid, note,
        requirements_met: requirements.map(requirement => ({ requirement, state: reads[requirement]?.state, met: reads[requirement]?.state === "met", evidence: reads[requirement]?.evidence.trim() ?? "" })),
      }).then(onClose).catch(() => undefined);
    }}>
    {q.isLoading && <Skeleton rows={2} />}
    <Banner error={q.error} onRetry={() => void q.refetch()} />
    {requirements.map((requirement, index) => {
      const row = reads[requirement] ?? { state: "", evidence: "" };
      return <fieldset key={requirement}>
        <legend>{requirement}</legend>
        <Field label={`Status — ${requirement}`} id={`${prefix}-${index}-state`} required>
          <select id={`${prefix}-${index}-state`} value={row.state} onChange={event => setReads({ ...reads, [requirement]: { ...row, state: event.target.value as RequirementState } })}>
            <option value="">Choose a status</option><option value="met">Met</option><option value="partial">Partial</option><option value="no">No</option>
          </select>
        </Field>
        <Field label={`Evidence — ${requirement}`} id={`${prefix}-${index}-evidence`} required>
          <textarea id={`${prefix}-${index}-evidence`} rows={3} maxLength={2000} value={row.evidence} onChange={event => {
            event.currentTarget.setCustomValidity(event.target.value && !event.target.value.trim() ? "Record the evidence for this requirement." : "");
            setReads({ ...reads, [requirement]: { ...row, evidence: event.target.value } });
          }} />
        </Field>
      </fieldset>;
    })}
    {q.data && requirements.length === 0 && <p className="muted">No must-haves were marked at intake. Record your overall read below.</p>}
  </ReasonDialog>;
}

export function RequirementsSection({ rid, aid, name, reads, canQualify }: { rid: string; aid: string; name: string; reads: RequirementRead[] | null; canQualify: boolean }) {
  const [editing, setEditing] = useState(false);
  return <section>
    <div className="hrow"><h2 className="sec">Requirements met</h2>{canQualify && <button className="btn" onClick={() => setEditing(true)}>Qualify</button>}</div>
    {!reads?.length ? <p className="muted">No requirements assessment recorded.</p> : <Table label="Requirements met" mobile="stack" head={<tr><th>Requirement</th><th>Status</th><th>Evidence</th></tr>}>
      {reads.map((row, index) => {
        const state = row["state"] ?? (row["met"] === true ? "met" : row["met"] === false ? "no" : null);
        const label = state === "met" ? "Met" : state === "partial" ? "Partial" : state === "no" ? "No" : "Not recorded";
        return <tr key={index}><td>{String(row["requirement"] ?? "")}</td><td><Pill tone={state === "met" ? "ok" : "warn"}>{label}</Pill></td><td>{String(row["evidence"] ?? "")}</td></tr>;
      })}
    </Table>}
    {editing && <QualifyDialog rid={rid} aid={aid} name={name} onClose={() => setEditing(false)} />}
  </section>;
}
